import type { FrontendAccessPolicy, PlatformEntryContext, User } from '@/types';
import {
  canAccessKpiAnalytics,
  getAuthorizedEntryRoute,
  narrowAccessPolicyToContour,
} from '@/utils/access';
import { withActiveContour } from '@/utils/activeContour';
import { KPI_ANALYTICS_ROUTE } from '@/utils/kpiAdminAccess';

export type KpiWorkingRouteDecision = {
  allowed: boolean;
  redirectTo: string;
};

export function resolveKpiWorkingRouteAccess({
  user,
  accessPolicy,
  entryContext,
  activeContour,
  requiresActiveContour = false,
}: {
  user: Pick<User, 'role' | 'access_mode' | 'access_status'> | null | undefined;
  accessPolicy: FrontendAccessPolicy | null | undefined;
  entryContext: PlatformEntryContext | null | undefined;
  activeContour: string | null | undefined;
  requiresActiveContour?: boolean;
}): KpiWorkingRouteDecision {
  const normalizedContour = String(activeContour || '').trim().toLowerCase();
  const contourAllowed = Boolean(
    normalizedContour
    && entryContext?.contours.some(
      (contour) => contour.tenant_id.trim().toLowerCase() === normalizedContour,
    ),
  );
  if ((requiresActiveContour || entryContext?.entry_kind === 'root_admin') && !contourAllowed) {
    const redirectTo = entryContext?.entry_kind === 'root_admin'
      ? '/admin/access'
      : entryContext?.entry_kind === 'multi_contour'
        ? '/select-contour'
        : '/';
    return { allowed: false, redirectTo };
  }

  const explicitAccount = user?.access_mode === 'explicit' || accessPolicy?.access_mode === 'explicit';
  if (!explicitAccount) {
    const allowed = canAccessKpiAnalytics(user?.role);
    return {
      allowed,
      redirectTo: allowed ? KPI_ANALYTICS_ROUTE : '/dashboard',
    };
  }

  if (
    user?.access_mode !== 'explicit'
    || user.access_status !== 'active'
    || accessPolicy?.access_mode !== 'explicit'
  ) {
    return { allowed: false, redirectTo: '/access-pending' };
  }

  if (!entryContext || entryContext.entry_kind === 'pending') {
    return { allowed: false, redirectTo: '/access-pending' };
  }

  if (!contourAllowed) {
    const redirectTo = entryContext.entry_kind === 'root_admin'
      ? '/admin/access'
      : entryContext.entry_kind === 'multi_contour'
        ? '/select-contour'
        : '/';
    return { allowed: false, redirectTo };
  }

  const contourPolicy = narrowAccessPolicyToContour(accessPolicy, normalizedContour);
  const allowed = canAccessKpiAnalytics(user.role, contourPolicy);
  if (allowed) {
    return { allowed: true, redirectTo: KPI_ANALYTICS_ROUTE };
  }
  const fallbackRoute = getAuthorizedEntryRoute(user, contourPolicy, '/dashboard');
  return {
    allowed,
    redirectTo: fallbackRoute === '/access-pending'
      ? fallbackRoute
      : withActiveContour(fallbackRoute, normalizedContour),
  };
}
